import React from 'react';
import { IntlShape } from 'react-intl';
import { getLocale, INTL_LOCALES } from '@raisin/cp-utils';
import { DBSProduct, OFFER_PRODUCT_TYPES } from 'types';
import { formatTerm } from '../../../../../utils/bankPageUtils/bankOffers';
import { getMarketingProductType } from '../../../../../utils/getMarketingProductType';
import { isCallalikeSubtype } from '../../../../../utils/helpers';
import messages from '../messages';
import { OfferDetailConfig } from '../types';
import DocumentLink from './DocumentLink';
import {
  formatAvailabilityValue,
  formatSettlementDayUnits,
  formatSettlementValue,
  getEarlyTerminationRate,
  getInterestCreditValue,
  getInterestCreditVisibility,
  getInterestPayoutValue,
  getInvestmentAmountRangeValue,
  getRenewalMessage,
  getRepresentativeValue,
} from './helpers';

const DOCUMENT_TYPES = {
  not_applicable: messages.noAdditionalDocument,
  digital: messages.digitalDocument,
  physical: messages.physicalDocument,
};

export const getConfigs = (t, formatNumber, locale, formatDate) => ({
  GBR: (offer: DBSProduct): OfferDetailConfig => {
    const { compliance } = offer;

    return {
      tabs: [
        {
          id: 'summary',
          title: t(messages.bankOfferDetailsSummary),
          fields: [
            {
              id: messages.bankOfferDetailsAccountName,
              title:
                compliance?.saving_product_name?.question ??
                t(messages.bankOfferDetailsAccountName),
              fullWidth: true,
              isVisible: true,
              value: compliance?.saving_product_name?.answer ?? '95 Day Notice Account',
              isHTMLContentAvailable: Boolean(offer?.compliance?.saving_product_name),
            },
            {
              id: messages.bankOfferDetailsInterestRate,
              title:
                compliance?.interest_profit_rate?.question ??
                t(messages.bankOfferDetailsInterestRateTitle),
              fullWidth: true,
              isVisible: true,
              isHTMLContentAvailable: Boolean(offer?.compliance?.interest_profit_rate),
              value:
                compliance?.interest_profit_rate?.answer ??
                t(messages.bankOfferDetailsInterestRate, {
                  interest_rate_effective: formatNumber(
                    offer?.interest_rates?.interest_rate_effective,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    },
                  ),
                  interest_rate_nominal: formatNumber(
                    offer?.interest_rates?.interest_rate_nominal,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    },
                  ),
                }),
            },
            {
              id: messages.bankOfferDetailsChangeRateTitle,
              title:
                compliance?.change_interest_profit_rate?.question ??
                t(messages.bankOfferDetailsChangeRateTitle, {
                  bank_name: offer?.deposit_taking_bank?.name,
                }),
              fullWidth: true,
              isVisible: true,
              isHTMLContentAvailable: Boolean(offer?.compliance?.change_interest_profit_rate),
              value:
                compliance?.change_interest_profit_rate?.answer ??
                'The interest rate applicable to your deposit is variable.\n\n Should the bank set a date to lower the interest rate, you will be given a minimum of 95 days’ notice. Should the bank set a date to increase the interest rate, customers will be notified within 30 days of the increase.\n\n Please review the Meteor Asset Management (MAM) Terms and Conditions for more information.',
            },
            {
              id: messages.bankOfferDetailsEstimatedBalanceTitle,
              title:
                compliance?.how_much_profit?.question ??
                t(messages.bankOfferDetailsEstimatedBalanceTitle),
              fullWidth: true,
              isVisible: true,
              isHTMLContentAvailable: Boolean(offer?.compliance?.how_much_profit),
              value:
                compliance?.how_much_profit?.answer ??
                'The estimated balance after 12 months on a deposit of £1,000 is £1,053.05. This projection is for illustrative purposes only and does not take into account individual circumstances',
            },
            {
              id: messages.bankOfferDetailsHowToTitle,
              title:
                compliance?.how_manage_savings?.question ?? t(messages.bankOfferDetailsHowToTitle),
              fullWidth: true,
              isVisible: true,
              isHTMLContentAvailable: Boolean(offer?.compliance?.how_manage_savings),
              value:
                compliance?.how_manage_savings?.answer ??
                'To open this savings account, you must apply online through Raisin UK. Savings accounts at Raisin UK are only available through this online application process, and to be eligible to apply, you must:\n \n ● Be a UK resident who is aged 18 or over\n \n ● Hold a UK bank or building society account from which you will fund your application\n \n ● Have a valid mobile phone number and email address\n \n ● Have a UK National Insurance Number\n \n Your application acceptance will be subject to the successful completion of an Anti-Money Laundering/Know Your Customer (AML/KYC) check.\n \n The minimum amount you can deposit is £1,000, and the maximum amount you can deposit is £85,000 (per product and banking institution).\n \n You can manage your savings account through Raisin UK by logging into your Raisin UK Account. You can contact Raisin UK by telephone, by email or in writing by using the contact details provided in the Raisin UK Terms & Conditions.\n \n Please ensure you have read and understood all sets of Terms and Conditions before applying for this product.',
            },
            {
              id: messages.bankOfferDetailsWithdrawalTitle,
              title:
                compliance?.withdraw_money?.question ?? t(messages.bankOfferDetailsWithdrawalTitle),
              fullWidth: true,
              isVisible: true,
              isHTMLContentAvailable: Boolean(offer?.compliance?.withdraw_money),
              value:
                compliance?.withdraw_money?.answer ??
                'Because this savings account is a fixed term deposit, withdrawals are not permitted before the maturity date, except if an account holder dies, becomes mentally incapacitated or bankrupt, or in any other exceptional circumstances agreed to by Raisin in its absolute discretion. This savings account does NOT operate on the basis that you can end the fixed term of your savings account prematurely and pay a fee to withdraw your funds sooner.\n \n Your savings account will not automatically renew at the end of the fixed term for a further fixed term.',
            },
            {
              id: messages.bankOfferDetailsAdditionalInfoTitle,
              title:
                compliance?.information_required?.question ??
                t(messages.bankOfferDetailsAdditionalInfoTitle),
              fullWidth: true,
              isVisible: true,
              isHTMLContentAvailable: Boolean(offer?.compliance?.information_required),
              value:
                compliance?.information_required?.answer ??
                'Meteor Asset Management (MAM), with their administration arm Meteor Investment Management (MIM), will administer the deposit on your behalf.\n \n Funds will appear in MIM’s account by the following business day from funding your application at the latest. Funds will be remitted to LHV Bank by MIM on the next business day after arriving in the MIM account. It can therefore take up to 2 business days for an ordered product to be set up and start earning interest.\n \n All cash in transit and deposits once established, will be held by MIM and MAM in trust for you under FCA client money and asset rules.',
            },
          ],
        },
      ],
    };
  },
  AUT: (offer: DBSProduct): OfferDetailConfig => ({
    tabs: [
      {
        id: 'interest',
        title: t(messages.bankOfferDetailsInterestTabTitle),
        fields: [
          {
            id: 'nominal_interest_rate',
            title: t(messages.bankOfferDetailsInterestRateNominal),
            fullWidth: false,
            isVisible: true,
            value: t(messages.bankOfferDetailsRate, {
              rate: formatNumber(offer.interest_rates.interest_rate_nominal, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }),
          },
          {
            id: 'effective_interest_rate',
            title: t(messages.bankOfferDetailsInterestRateEffective),
            fullWidth: false,
            isVisible: true,
            value: t(messages.bankOfferDetailsRate, {
              rate: formatNumber(offer.interest_rates.interest_rate_effective, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }),
          },
          {
            id: 'compound_interest',
            title: t(messages.bankOfferDetailsCompoundInterest),
            fullWidth: false,
            isVisible: true,
            value: offer.conditions.annuity.compound_interest
              ? t(messages.bankOfferDetailsYes)
              : t(messages.bankOfferDetailsNo),
          },
          {
            id: 'term',
            title: t(messages.bankOfferDetailsTerm),
            fullWidth: false,
            isVisible: !!offer.term?.period,
            value: formatTerm(
              offer,
              {
                formatNumber,
                locale,
                formatDate,
              },
              t,
              { renderWeeks: true },
            ),
          },
          {
            id: 'availability',
            title: t(messages.bankOfferDetailsAvailability),
            fullWidth: true,
            isVisible: true,
            value: isCallalikeSubtype(
              getMarketingProductType(offer),
              offer.conditions?.daily_settlement,
            )
              ? formatSettlementValue({
                  t,
                  messages,
                  settlementDay: offer.conditions?.settlement_day,
                  formatSettlementDayUnits,
                })
              : formatAvailabilityValue({
                  t,
                  messages,
                  contractualTerminationAllowed:
                    offer.conditions?.termination?.contractual_termination_allowed,
                  productType: getMarketingProductType(offer),
                  noticePeriod: offer.conditions?.termination?.notice_period,
                }),
          },
          {
            id: 'interest_credit',
            title: t(messages.bankOfferDetailsInterestCredit),
            fullWidth: false,
            isVisible: getInterestCreditVisibility(getMarketingProductType(offer), locale),
            value: getInterestCreditValue(
              getMarketingProductType(offer),
              offer.conditions.annuity,
              t,
            ),
          },
          {
            id: 'interest_payment',
            title: t(messages.bankOfferDetailsInterestPayment),
            fullWidth: false,
            isVisible:
              getMarketingProductType(offer) !== OFFER_PRODUCT_TYPES.OVERNIGHT &&
              getMarketingProductType(offer) !== OFFER_PRODUCT_TYPES.NOTICE,
            value: t(
              getInterestPayoutValue(getMarketingProductType(offer), offer.conditions.annuity),
            ),
          },
          {
            id: 'interest_rate_changes',
            title: t(messages.bankOfferDetailsInterestRateChanges),
            fullWidth: false,
            isVisible: true,
            value: t(
              getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.TERM
                ? messages.interestRateChangesFixedTerm
                : messages.interestRateChangesOthers,
            ),
          },
          {
            id: 'renewal',
            title: t(messages.bankOfferDetailsRenewal),
            fullWidth: true,
            isVisible: !(
              getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.OVERNIGHT ||
              getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.NOTICE
            ),
            value: t(getRenewalMessage(offer.conditions.renewal)),
          },
        ],
      },
      {
        id: 'invest',
        title: t(messages.bankOfferDetailsInvestTabTitle),
        fields: [
          {
            id: 'currency',
            title: t(messages.currency),
            fullWidth: false,
            isVisible: true,
            value: offer.currency,
          },
          {
            id: 'investment-amount-range',
            isVisible:
              Number.isFinite(+(offer.conditions.opening.minimum_amount || '')) &&
              Number.isFinite(+offer.conditions.maximum_balance),
            fullWidth: false,
            title: t(messages.possibleInvestmentAmount),
            value: getInvestmentAmountRangeValue(
              offer.conditions.opening.minimum_amount || '',
              offer.conditions.maximum_balance,
              offer.currency,
              formatNumber,
            ),
          },
          {
            id: 'costs',
            fullWidth: false,
            isVisible: true,
            title: t(messages.costs),
            value: t(messages.noCost),
          },
          {
            id: 'withholding-tax',
            fullWidth: false,
            isVisible:
              ![INTL_LOCALES.POLAND, INTL_LOCALES.UK].includes(locale) &&
              !!offer.deposit_taking_bank?.branch_country?.source_tax_description,
            title: t(messages.withholdingTax),
            value: offer.deposit_taking_bank?.branch_country?.source_tax_description,
          },
          {
            id: 'document',
            fullWidth: false,
            isVisible: true,
            title: t(messages.documentsToBeSubmitted),
            value: t(
              DOCUMENT_TYPES[offer.conditions.opening.application_document ?? 'not_applicable'],
            ),
          },
        ],
      },
    ],
  }),
  ESP: (offer: DBSProduct): OfferDetailConfig => ({
    tabs: [
      {
        id: 'interest',
        title: t(messages.bankOfferDetailsInterestTabTitle),
        fields: [
          {
            id: 'nominal_interest_rate',
            title: t(messages.bankOfferDetailsInterestRateNominal),
            fullWidth: false,
            isVisible: true,
            value: t(messages.bankOfferDetailsRate, {
              rate: formatNumber(offer.interest_rates.interest_rate_nominal, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }),
          },
          {
            id: 'effective_interest_rate',
            title: t(messages.bankOfferDetailsInterestRateEffective),
            fullWidth: false,
            isVisible: true,
            value: t(messages.bankOfferDetailsRate, {
              rate: formatNumber(offer.interest_rates.interest_rate_effective, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }),
          },
          {
            id: 'compound_interest',
            title: t(messages.bankOfferDetailsCompoundInterest),
            fullWidth: false,
            isVisible: true,
            value: offer.conditions.annuity.compound_interest
              ? t(messages.bankOfferDetailsYes)
              : t(messages.bankOfferDetailsNo),
          },
          {
            id: 'term',
            title: t(messages.bankOfferDetailsTerm),
            fullWidth: false,
            isVisible: !!offer.term?.period,
            value: formatTerm(
              offer,
              {
                formatNumber,
                locale,
                formatDate,
              },
              t,
              { renderWeeks: true },
            ),
          },
          {
            id: 'availability',
            title: t(messages.bankOfferDetailsAvailability),
            fullWidth: true,
            isVisible: true,
            value: isCallalikeSubtype(
              getMarketingProductType(offer),
              offer.conditions?.daily_settlement,
            )
              ? formatSettlementValue({
                  t,
                  messages,
                  settlementDay: offer.conditions?.settlement_day,
                  formatSettlementDayUnits,
                })
              : formatAvailabilityValue({
                  t,
                  messages,
                  contractualTerminationAllowed:
                    offer.conditions?.termination?.contractual_termination_allowed,
                  productType: getMarketingProductType(offer),
                  noticePeriod: offer.conditions?.termination?.notice_period,
                }),
          },
          {
            id: 'interest_credit',
            title: t(messages.bankOfferDetailsInterestCredit),
            fullWidth: false,
            isVisible: getInterestCreditVisibility(getMarketingProductType(offer), locale),
            value: getInterestCreditValue(
              getMarketingProductType(offer),
              offer.conditions.annuity,
              t,
            ),
          },
          {
            id: 'interest_payment',
            title: t(messages.bankOfferDetailsInterestPayment),
            fullWidth: false,
            isVisible:
              getMarketingProductType(offer) !== OFFER_PRODUCT_TYPES.OVERNIGHT &&
              getMarketingProductType(offer) !== OFFER_PRODUCT_TYPES.NOTICE,
            value: t(
              getInterestPayoutValue(getMarketingProductType(offer), offer.conditions.annuity),
            ),
          },
          {
            id: 'interest_rate_changes',
            title: t(messages.bankOfferDetailsInterestRateChanges),
            fullWidth: false,
            isVisible: true,
            value: t(
              getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.TERM
                ? messages.interestRateChangesFixedTerm
                : messages.interestRateChangesOthers,
            ),
          },
          {
            id: 'representative-example',
            title: t(messages.bankOfferDetailsRepresentativeLabel),
            fullWidth: true,
            isVisible: true,
            value: getRepresentativeValue(offer, messages, t, formatNumber),
          },
          {
            id: 'renewal',
            title: t(messages.bankOfferDetailsRenewal),
            fullWidth: true,
            isVisible: !(
              getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.OVERNIGHT ||
              getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.NOTICE
            ),
            value: t(getRenewalMessage(offer.conditions.renewal)),
          },
          {
            id: 'product_info_sheet',
            title: null,
            fullWidth: false,
            isVisible: true,
            value: <DocumentLink offer={offer} />,
          },
        ],
      },
      {
        id: 'invest',
        title: t(messages.bankOfferDetailsInvestTabTitle),
        fields: [
          {
            id: 'currency',
            title: t(messages.currency),
            fullWidth: false,
            isVisible: true,
            value: offer.currency,
          },
          {
            id: 'investment-amount-range',
            isVisible:
              Number.isFinite(+(offer.conditions.opening.minimum_amount || '')) &&
              Number.isFinite(+offer.conditions.maximum_balance),
            fullWidth: false,
            title: t(messages.possibleInvestmentAmount),
            value: getInvestmentAmountRangeValue(
              offer.conditions.opening.minimum_amount || '',
              offer.conditions.maximum_balance,
              offer.currency,
              formatNumber,
            ),
          },
          {
            id: 'costs',
            fullWidth: false,
            isVisible: true,
            title: t(messages.costs),
            value: t(messages.noCost),
          },
          {
            id: 'withholding-tax',
            fullWidth: false,
            isVisible:
              ![INTL_LOCALES.POLAND, INTL_LOCALES.UK].includes(locale) &&
              !!offer.deposit_taking_bank?.branch_country?.source_tax_description,
            title: t(messages.withholdingTax),
            value: offer.deposit_taking_bank?.branch_country?.source_tax_description,
          },
          {
            id: 'document',
            fullWidth: false,
            isVisible: true,
            title: t(messages.documentsToBeSubmitted),
            value: t(
              DOCUMENT_TYPES[offer.conditions.opening.application_document ?? 'not_applicable'],
            ),
          },
        ],
      },
    ],
  }),
  default: (offer: DBSProduct): OfferDetailConfig => ({
    tabs: [
      {
        id: 'interest',
        title: t(messages.bankOfferDetailsInterestTabTitle),
        fields: [
          {
            id: 'nominal_interest_rate',
            title: t(messages.bankOfferDetailsInterestRateNominal),
            fullWidth: false,
            isVisible: true,
            value: t(messages.bankOfferDetailsRate, {
              rate: formatNumber(offer.interest_rates.interest_rate_nominal, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }),
          },
          {
            id: 'effective_interest_rate',
            title: t(messages.bankOfferDetailsInterestRateEffective),
            fullWidth: false,
            isVisible: true,
            value: t(messages.bankOfferDetailsRate, {
              rate: formatNumber(offer.interest_rates.interest_rate_effective, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }),
          },
          {
            id: 'early_termination_interest_rate',
            title: t(messages.bankOfferDetailsEarlyTerminationInterestRate),
            fullWidth: false,
            isVisible: !!(
              offer.product_type === 'TERM' &&
              offer.term?.period &&
              offer.conditions.termination.contractual_termination_allowed
            ),
            value: t(messages.bankOfferDetailsRate, {
              rate: formatNumber(getEarlyTerminationRate(offer, locale), {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
            }),
          },
          {
            id: 'compound_interest',
            title: t(messages.bankOfferDetailsCompoundInterest),
            fullWidth: false,
            isVisible: true,
            value: offer.conditions.annuity.compound_interest
              ? t(messages.bankOfferDetailsYes)
              : t(messages.bankOfferDetailsNo),
          },
          {
            id: 'term',
            title: t(messages.bankOfferDetailsTerm),
            fullWidth: false,
            isVisible: !!offer.term?.period,
            value: formatTerm(
              offer,
              {
                formatNumber,
                locale,
                formatDate,
              },
              t,
              { renderWeeks: true },
            ),
          },
          {
            id: 'availability',
            title: t(messages.bankOfferDetailsAvailability),
            fullWidth: true,
            isVisible: true,
            value: isCallalikeSubtype(
              getMarketingProductType(offer),
              offer.conditions?.daily_settlement,
            )
              ? formatSettlementValue({
                  t,
                  messages,
                  settlementDay: offer.conditions?.settlement_day,
                  formatSettlementDayUnits,
                })
              : formatAvailabilityValue({
                  t,
                  messages,
                  contractualTerminationAllowed:
                    offer.conditions?.termination?.contractual_termination_allowed,
                  productType: getMarketingProductType(offer),
                  noticePeriod: offer.conditions?.termination?.notice_period,
                }),
          },
          {
            id: 'interest_credit',
            title: t(messages.bankOfferDetailsInterestCredit),
            fullWidth: false,
            isVisible: getInterestCreditVisibility(getMarketingProductType(offer), locale),
            value: getInterestCreditValue(
              getMarketingProductType(offer),
              offer.conditions.annuity,
              t,
            ),
          },
          {
            id: 'interest_payment',
            title: t(messages.bankOfferDetailsInterestPayment),
            fullWidth: false,
            isVisible:
              getMarketingProductType(offer) !== OFFER_PRODUCT_TYPES.OVERNIGHT &&
              getMarketingProductType(offer) !== OFFER_PRODUCT_TYPES.NOTICE,
            value: t(
              getInterestPayoutValue(getMarketingProductType(offer), offer.conditions.annuity),
            ),
          },
          {
            id: 'interest_rate_changes',
            title: t(messages.bankOfferDetailsInterestRateChanges),
            fullWidth: false,
            isVisible: true,
            value: t(
              getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.TERM
                ? messages.interestRateChangesFixedTerm
                : messages.interestRateChangesOthers,
            ),
          },
          {
            id: 'renewal',
            title: t(messages.bankOfferDetailsRenewal),
            fullWidth: true,
            isVisible: !(
              getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.OVERNIGHT ||
              getMarketingProductType(offer) === OFFER_PRODUCT_TYPES.NOTICE
            ),
            value: t(getRenewalMessage(offer.conditions.renewal)),
          },
          {
            id: 'product_info_sheet',
            title: null,
            fullWidth: false,
            isVisible: true,
            value: <DocumentLink offer={offer} />,
          },
        ],
      },
      {
        id: 'invest',
        title: t(messages.bankOfferDetailsInvestTabTitle),
        fields: [
          {
            id: 'currency',
            title: t(messages.currency),
            fullWidth: false,
            isVisible: true,
            value: offer.currency,
          },
          {
            id: 'investment-amount-range',
            isVisible:
              Number.isFinite(+(offer.conditions.opening.minimum_amount || '')) &&
              Number.isFinite(+offer.conditions.maximum_balance),
            fullWidth: false,
            title: t(messages.possibleInvestmentAmount),
            value: getInvestmentAmountRangeValue(
              offer.conditions.opening.minimum_amount || '',
              offer.conditions.maximum_balance,
              offer.currency,
              formatNumber,
            ),
          },
          {
            id: 'costs',
            fullWidth: false,
            isVisible: true,
            title: t(messages.costs),
            value: t(messages.noCost),
          },
          {
            id: 'withholding-tax',
            fullWidth: false,
            isVisible:
              ![INTL_LOCALES.POLAND, INTL_LOCALES.UK].includes(locale) &&
              !!offer.deposit_taking_bank?.branch_country?.source_tax_description,
            title: t(messages.withholdingTax),
            value: offer.deposit_taking_bank?.branch_country?.source_tax_description,
          },
          {
            id: 'document',
            fullWidth: false,
            isVisible: true,
            title: t(messages.documentsToBeSubmitted),
            value: t(
              DOCUMENT_TYPES[offer.conditions.opening.application_document ?? 'not_applicable'],
            ),
          },
        ],
      },
    ],
  }),
});

export const getDetailConfig = (
  offer: DBSProduct,
  t,
  formatNumber: IntlShape['formatNumber'],
  locale: string,
  formatDate?: IntlShape['formatDate'],
): OfferDetailConfig => {
  const DEFAULT_LOCALE = INTL_LOCALES.UK;

  const currentLocale = getLocale(locale || DEFAULT_LOCALE)?.iso3.toUpperCase();
  const AllConfigs = getConfigs(t, formatNumber, locale, formatDate);
  return AllConfigs?.[`${currentLocale}`]
    ? AllConfigs?.[`${currentLocale}`](offer)
    : AllConfigs.default(offer);
};
